// Shared filter state for the Game Report, persisted in the URL query string.
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CurrencyMode } from '../CurrencySelector';

export type GameSubTab = 'overview' | 'games' | 'brands' | 'mix' | 'top';

export interface GameFilters {
  subTab: GameSubTab;
  dateFrom: string;
  dateTo: string;
  siteIds: number[];
  currencyMode: CurrencyMode;
  granularity: 'daily' | 'weekly' | 'monthly';
  gameType: string | undefined;
  brandId: string | undefined;
  dimensions: string[];
  topN: number;
  // Setters
  setSubTab: (v: GameSubTab) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setDateRange: (from: string, to: string) => void;
  setSiteIds: (v: number[]) => void;
  setCurrencyMode: (v: CurrencyMode) => void;
  setGranularity: (v: 'daily' | 'weekly' | 'monthly') => void;
  setGameType: (v: string | undefined) => void;
  setBrandId: (v: string | undefined) => void;
  setDimensions: (v: string[]) => void;
  setTopN: (v: number) => void;
}

function defaultDateFrom(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function defaultDateTo(): string {
  return new Date().toISOString().split('T')[0];
}

export function useGameFilters(): GameFilters {
  const [params, setParams] = useSearchParams();

  const patch = useCallback((updates: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    });
  }, [setParams]);

  return useMemo<GameFilters>(() => {
    const subTabRaw = params.get('view') ?? 'overview';
    const subTab: GameSubTab = (['overview', 'games', 'brands', 'mix', 'top'] as const).includes(subTabRaw as GameSubTab)
      ? (subTabRaw as GameSubTab)
      : 'overview';

    const dateFrom = params.get('from') || defaultDateFrom();
    const dateTo = params.get('to') || defaultDateTo();

    const siteIdsStr = params.get('site_ids') || '';
    const siteIds = siteIdsStr
      ? siteIdsStr.split(',').map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n))
      : [];

    const currencyRaw = params.get('display_currency') || 'EUR';
    const currencyMode: CurrencyMode = (['EUR', 'USD', 'BASE'] as const).includes(currencyRaw as CurrencyMode)
      ? (currencyRaw as CurrencyMode)
      : 'EUR';

    const granRaw = params.get('granularity') || 'daily';
    const granularity: 'daily' | 'weekly' | 'monthly' =
      granRaw === 'weekly' || granRaw === 'monthly' ? granRaw : 'daily';

    const gameTypeRaw = params.get('game_type') || '';
    const gameType = gameTypeRaw || undefined;

    const brandIdRaw = params.get('brand_id') || '';
    const brandId = brandIdRaw || undefined;

    const dimsStr = params.get('dimensions');
    const dimensions = dimsStr !== null
      ? dimsStr.split(',').filter(Boolean)
      : ['site', 'game'];

    const topNRaw = params.get('top_n');
    const topN = topNRaw ? parseInt(topNRaw, 10) || 20 : 20;

    return {
      subTab, dateFrom, dateTo, siteIds, currencyMode, granularity, gameType, brandId, dimensions, topN,
      setSubTab: (v) => patch({ view: v === 'overview' ? null : v }),
      setDateFrom: (v) => patch({ from: v }),
      setDateTo: (v) => patch({ to: v }),
      setDateRange: (from, to) => patch({ from, to }),
      setSiteIds: (v) => patch({ site_ids: v.length ? v.join(',') : null }),
      setCurrencyMode: (v) => patch({ display_currency: v }),
      setGranularity: (v) => patch({ granularity: v === 'daily' ? null : v }),
      setGameType: (v) => patch({ game_type: v ?? null }),
      setBrandId: (v) => patch({ brand_id: v ?? null }),
      setDimensions: (v) => {
        const isDefault = v.length === 2 && v.includes('site') && v.includes('game');
        return patch({ dimensions: isDefault ? null : v.join(',') });
      },
      setTopN: (v) => patch({ top_n: v === 20 ? null : String(v) }),
    };
  }, [params, patch]);
}
